import type { ErrorStage } from "./error-handler";

/** Стадии, на которых ошибка вообще имеет смысл как сигнал о поломке. */
const REPORTABLE_STAGES: ReadonlySet<string> = new Set<ErrorStage>([
  "server_error",
  "react_error",
  "no_connection",
  "request_sent_no_response",
]);

/** Коды axios, по которым видно: связь действительно не сложилась. */
const NETWORK_CODES: ReadonlySet<string> = new Set([
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ECONNREFUSED",
  "ECONNRESET",
  "ERR_INTERNET_DISCONNECTED",
]);

const NETWORK_MESSAGES = [
  "network error",
  "failed to fetch",
  "timeout of",
  "load failed",
  "socket hang up",
];

/**
 * Ошибка и правда о связи, а не просто брошенный кем-то `Error` (KAN-259).
 *
 * `normalizeAxiosError` ставит стадию `no_connection` по отсутствию `request` и
 * `response` — но у обычного `new Error("Неверный пароль")` их нет по той же
 * причине, по которой их нет у оборванного соединения: это не axios-ошибка.
 * Поэтому смотрим на признаки транспорта отдельно.
 */
function looksNetworkRelated(original: unknown, code?: string): boolean {
  if (code && NETWORK_CODES.has(code.toUpperCase())) return true;

  const raw = original as { request?: unknown; response?: unknown; code?: string } | null;
  if (raw && (raw.request !== undefined || raw.response !== undefined)) return true;
  if (raw?.code && NETWORK_CODES.has(raw.code.toUpperCase())) return true;

  const message = original instanceof Error ? original.message.toLowerCase() : "";
  return NETWORK_MESSAGES.some((needle) => message.includes(needle));
}

/**
 * Отправлять ли ошибку в GlitchTip (KAN-259).
 *
 * Правило «шлём только server_error и выше» было в коде и раньше, но мимо него
 * проходила целая категория: ожидаемые бизнес-ошибки. Логин с неверным паролем,
 * занятое имя пользователя, «Игрок не найден» — всё это прилетало в трекер со
 * стадией `no_connection`, потому что наверх бросается голый `Error`, у
 * которого нет ни `request`, ни `response`. Тридцать два события в ленте,
 * которые никогда не были багом.
 *
 * Сетевые обрывы под ту же стадию попадают заслуженно и по-прежнему шлются:
 * их отличает наличие транспорта или сетевой код.
 */
export function shouldReportError(
  normalized: { stage?: ErrorStage; code?: string },
  original: unknown,
): boolean {
  const stage = normalized.stage;
  if (!stage || !REPORTABLE_STAGES.has(stage)) return false;

  if (stage === "no_connection") {
    return looksNetworkRelated(original, normalized.code);
  }

  return true;
}

/** Статусы, которыми отвечает прокси, когда стенд не успевает или лежит. */
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 504]);

/**
 * Сетевой шум тест-стенда: таймаут axios, 502/504 от прокси (KAN-259).
 *
 * Стенд живёт в VM на домашнем хосте и регулярно отвечает таймаутом или
 * шлюзовой ошибкой — это состояние железа, а не баг. На проде те же события —
 * сигнал, поэтому фильтр срабатывает только при environment `home-staging`.
 * Настоящая 500 на стенде не отбрасывается: это ошибка кода.
 */
export function isStagingNetworkNoise(environment: string | undefined, original: unknown): boolean {
  if (environment !== "home-staging") return false;

  const raw = original as { code?: string; response?: { status?: number } } | null;
  const status = raw?.response?.status;
  if (status !== undefined && GATEWAY_STATUSES.has(status)) return true;

  const code = raw?.code?.toUpperCase();
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") return true;

  return original instanceof Error && original.message.toLowerCase().startsWith("timeout of");
}
